import { SaveOutlined } from "@ant-design/icons"
import { App, Button, Drawer, Empty } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { MODAL_CONFIRM_CONFIG } from "../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { isFormChanged } from "../../../utils/object/is-form-changed.ts"
import { PermissionsContent } from "../../SettingsPermissions/components/PermissionsContent.tsx"
import {
  applyFieldsToPermissions,
  getBulkColumnChangeFields,
  getBulkFeaturesChangeFields,
  getValuesChangeFields
} from "../../SettingsPermissions/helpers/cascade.ts"
import type { NestedValues } from "../../SettingsPermissions/helpers/dependencies.ts"
import type {
  OnBulkChange,
  OnFeaturesToggle,
  OnItemChange
} from "../../SettingsPermissions/types.ts"
import type { PermissionFeatures } from "../../SettingsPermissions/types/permissions.types.ts"
import type { TeamMemberMutationData } from "../types/types.ts"

interface TeamMemberPermissionsDrawerProps {
  open: boolean
  memberName?: string
  initialPermissions?: PermissionFeatures
  saving: boolean
  onSave: (data: TeamMemberMutationData) => Promise<void>
  onClose: () => void
}

export function TeamMemberPermissionsDrawer({
  open,
  memberName,
  initialPermissions,
  saving,
  onSave,
  onClose
}: TeamMemberPermissionsDrawerProps) {
  const { t } = useTranslation()
  const { modal } = App.useApp()

  const [draftPermissions, setDraftPermissions] =
    useState<PermissionFeatures | null>(null)
  const [heading, setHeading] = useState<string | undefined>(undefined)

  // Snapshot on open only. The drawer stays mounted so it can slide out, and
  // the page clears the fetched member while it does — reading these props on
  // every render would empty the panel mid-animation.
  useEffect(() => {
    if (!open) return

    setDraftPermissions(
      initialPermissions ? structuredClone(initialPermissions) : null
    )
    setHeading(memberName)
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the member is snapshotted when the drawer opens; the page clears the fetched member while the drawer slides out
  }, [open])

  const handleClose = () => {
    const hasChanges =
      draftPermissions &&
      initialPermissions &&
      isFormChanged(draftPermissions, initialPermissions)

    if (hasChanges) {
      modal.confirm({ ...MODAL_CONFIRM_CONFIG.cancelChanges, onOk: onClose })
      return
    }

    onClose()
  }

  const handleApply = async () => {
    if (!draftPermissions) return

    try {
      await onSave({ permissions: draftPermissions } as TeamMemberMutationData)
    } catch {
      // the page rethrows on failure and surfaces the error globally;
      // swallowing here only keeps the drawer open for a retry
    }
  }

  // The functional updater keeps these three stable, so flipping one switch
  // re-renders only the section whose object actually changed.
  const handleItemChange: OnItemChange = useCallback(
    (parentKey, childKey, key, value) => {
      setDraftPermissions((prev) => {
        const current = prev as NestedValues | null
        if (!current) return prev

        const changedValues = {
          [parentKey]: { [childKey]: { [key as string]: value } }
        } as NestedValues
        const allValues = applyFieldsToPermissions(current, [
          {
            name: [parentKey as string, childKey as string, key as string],
            value,
            touched: true
          }
        ])
        const fields = getValuesChangeFields(changedValues, allValues)

        return applyFieldsToPermissions(
          allValues,
          fields
        ) as unknown as PermissionFeatures
      })
    },
    []
  )

  const handleBulkChange: OnBulkChange = useCallback(
    (parentKey, colKey, subKeys, value) => {
      setDraftPermissions((prev) => {
        const current = prev as NestedValues | null
        if (!current) return prev

        const fields = getBulkColumnChangeFields(
          parentKey as string,
          colKey,
          subKeys,
          value,
          current
        )
        if (fields.length === 0) return prev

        return applyFieldsToPermissions(
          current,
          fields
        ) as unknown as PermissionFeatures
      })
    },
    []
  )

  const handleFeaturesToggle: OnFeaturesToggle = useCallback(
    (parentKey, features, value, subKey) => {
      setDraftPermissions((prev) => {
        const current = prev as NestedValues | null
        if (!current) return prev

        const fields = getBulkFeaturesChangeFields(
          parentKey as string,
          features,
          value,
          current,
          subKey
        )
        if (fields.length === 0) return prev

        return applyFieldsToPermissions(
          current,
          fields
        ) as unknown as PermissionFeatures
      })
    },
    []
  )

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={
        heading
          ? t("permissions.individualPermissionsTitleFor", { name: heading })
          : t("permissions.individualPermissionsTitle")
      }
      width={860}
      extra={
        <Button
          icon={<SaveOutlined />}
          type={"primary"}
          disabled={saving || !draftPermissions}
          loading={saving}
          onClick={handleApply}
        >
          {t("permissions.individualPermissionsApply")}
        </Button>
      }
    >
      {draftPermissions ? (
        <PermissionsContent
          permissions={draftPermissions}
          onItemChange={handleItemChange}
          onBulkChange={handleBulkChange}
          onFeaturesToggle={handleFeaturesToggle}
          isUpdating={saving}
          containerClassName={"2xl:grid-cols-1"}
        />
      ) : (
        <Empty description={t("permissions.individualPermissionsEmpty")} />
      )}
    </Drawer>
  )
}
